import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../../common/types';
import type { OutreachPlannedState } from '../../outreachTypes';
import { upNextMessages as messages } from './upNextMessages';
/* eslint-disable css-modules/no-unused-class -- the shared chips of the Outreach area and one stylesheet for Up next */
import shared from '../../Outreach.module.scss';
import styles from './UpNext.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PlannedStateCellProps {
  state: OutreachPlannedState;
  /** "in 19 h" (`UntilText`), a day and time, or null: the line under the chip. */
  until: React.ReactNode;
  /** The People column puts the cell on the left (design s5). */
  left?: boolean;
  /** Render a `<span>`: the cell sits inside the People row button, which takes phrasing content only. */
  inline?: boolean;
}

function Typing({ text, positive }: { text: string; positive: boolean }) {
  return (
    <span className={clsx(styles.typing, positive && styles.typingPos)}>
      {text}
      <span className={styles.dots} aria-hidden>
        <i />
        <i />
        <i />
      </span>
    </span>
  );
}

/** The state of a planned pitch (design s5b): writing, waits for you, ready, scheduled, sending. */
const PlannedStateCellComponent: React.FC<PlannedStateCellProps> = ({ state, until, left = false, inline = false }) => {
  const intl = useIntl();
  const body = () => {
    switch (state) {
      case 'writing':
        return <Typing text={intl.formatMessage(messages.stateWriting)} positive={false} />;
      case 'sending':
        return (
          <>
            <Typing text={intl.formatMessage(messages.stateSending)} positive />
            <span className={styles.bar} aria-hidden>
              <i />
            </span>
          </>
        );
      case 'review':
        return <span className={clsx(shared.chip, shared.chipWarn)}>{intl.formatMessage(messages.stateReview)}</span>;
      case 'ready':
        return <span className={shared.chip}>{intl.formatMessage(messages.stateReady)}</span>;
      case 'scheduled':
      default:
        return (
          <>
            <span className={clsx(shared.chip, shared.chipVio)}>{intl.formatMessage(messages.stateScheduled)}</span>
            {typeof until === 'string' ? <small>{until}</small> : until}
          </>
        );
    }
  };
  const Tag = inline ? 'span' : 'div';
  return (
    <Tag className={clsx(styles.state, left && styles.stateLeft)} data-testid="planned-state" data-state={state}>
      {body()}
    </Tag>
  );
};

export const PlannedStateCell = TypedMemo(PlannedStateCellComponent);
